import React, { useState, useEffect } from "react";
import {
  Button,
  Drawer,
  Form,
  Checkbox,
  Input,
  Select,
  Switch,
  Table,
  Tooltip,
  FormInstance,
} from "antd";
import { EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { groupAndSortByKey } from 'common/utils/helpers';
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { Application, EditingRow, Permission, PermissionWithFlags, UpdatedPermission, Dashboard } from "./RoleInterfaces";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import ActionButton from "stories/components/ActionButton/ActionButton";


const { Option } = Select;
const { TextArea } = Input;

interface Handlers {
  closeEditDrawer: () => void;
  onFinishEditing: (
    values: any,
    id: string,
    updatedPermissions: UpdatedPermission[]
  ) => void;
  openEditDrawer?: () => void;
}

interface Values {
  editingForm: FormInstance;
  visibleEditDrawer: boolean;
  hasPermissions?: boolean;
  editing: { editingRow: EditingRow | null };
  editFormRef: any;
  permissionsList: Permission[];
  applicationsList: Application[];
  dashboardsList: Dashboard[];
}

interface EditRoleProps {
  handlers: Handlers;
  values: Values;
}

const EditRole: React.FC<EditRoleProps> = ({ handlers, values }) => {
  const { closeEditDrawer, onFinishEditing } = handlers;
  const {
    editingForm,
    visibleEditDrawer,
    editing,
    editFormRef,
    permissionsList,
    applicationsList,
    dashboardsList,
    hasPermissions
  } = values;

  const editingRow = editing?.editingRow;
  const [displayedPermissions, setDisplayedPermissions] = useState<PermissionWithFlags[]>([]);
  const [updatedPermissions, setUpdatedPermissions] = useState<UpdatedPermission[]>([]);
  useDrawerScrollMargin(editingRow?.id, visibleEditDrawer);
  const [displayedDashboards, setDisplayedDashboards] = useState<Dashboard[]>([]);

  useEffect(() => {
    let isMounted = true;
    if (visibleEditDrawer && editingRow) {
      const filtered = permissionsList.filter(
        (p) => p.customerApplication === editingRow.customerApplication
      );

      const appliedPermissions = editingRow.permissions || [];

      const hydratedPermissions: PermissionWithFlags[] = filtered.map((perm) => {
        const matched = appliedPermissions.find((p) => p.permissionId === perm.id);

        return {
          ...perm,
          read: matched?.read ?? false,
          create: matched?.create ?? false,
          edit: matched?.edit ?? false,
          delete: matched?.delete ?? false,
        };
      });

      const newUpdatedPermissions: UpdatedPermission[] = hydratedPermissions.map((perm) => ({
        permissionId: perm.id,
        read: !!perm.read,
        create: !!perm.create,
        edit: !!perm.edit,
        delete: !!perm.delete,
      }));

      if (isMounted) {
        setDisplayedPermissions(hydratedPermissions);
        setUpdatedPermissions(newUpdatedPermissions);

        const filteredDashboardsList = dashboardsList.filter(
          (dashboard) => dashboard.customerApplication === editingRow.customerApplication
        );

        setDisplayedDashboards(filteredDashboardsList);
      }

    }
    return () => {
      isMounted = false;
    };
  }, [visibleEditDrawer, editingRow, permissionsList, dashboardsList]);

  const onChangePermission = (e: CheckboxChangeEvent, permissionId: string) => {
    const field = e.target.name as keyof UpdatedPermission;
    const checked = e.target.checked;

    setDisplayedPermissions((prev) =>
      prev.map((perm) =>
        perm.id === permissionId ? { ...perm, [field]: checked } : perm
      )
    );

    setUpdatedPermissions((prev) => {
      const existing = prev.find((p) => p.permissionId === permissionId);
      if (existing) {
        return prev.map((p) =>
          p.permissionId === permissionId ? { ...p, [field]: checked } : p
        );
      } else {
        return [...prev, { permissionId, read: false, create: false, edit: false, delete: false, [field]: checked }];
      }
    });
  };


  const handleChangeApplication = (applicationId: string) => {
    const filtered = permissionsList.filter(
      (p) => p.customerApplication === applicationId
    );

    const newUpdated = filtered.map((p) => ({
      permissionId: p.id,
      read: false,
      create: false,
      edit: false,
      delete: false,
    }));

    setDisplayedPermissions(filtered);
    setUpdatedPermissions(newUpdated);
    editingForm.setFieldsValue({ customerApplicationValue: applicationId });

    const filteredDashboardsList = dashboardsList.filter(
      (dashboard) => dashboard.customerApplication === applicationId
    );

    setDisplayedDashboards(filteredDashboardsList);
    editingForm.setFieldsValue({ dashboardsValue: [] });
  };

  const permissionColumns = [
    {
      title: "",
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: "",
      dataIndex: "description",
      key: "description",
      render: (_: any, record: Permission) => (
        <Tooltip title={record.description} color="var(--tertiary-color)" placement="left">
          <InfoCircleOutlined style={{ color: "var(--tertiary-color)" }} />
        </Tooltip>
      ),
    },
    {
      title: "View",
      key: "read",
      render: (_: any, record: PermissionWithFlags) => (
        <Checkbox
          name="read"
          checked={record.read}
          disabled={!record.supportsRead}
          onChange={(e) => onChangePermission(e, record.id)}
        />
      ),
    },
    {
      title: "Create",
      key: "create",
      render: (_: any, record: PermissionWithFlags) => (
        <Checkbox
          name="create"
          checked={record.create}
          disabled={!record.supportsCreate}
          onChange={(e) => onChangePermission(e, record.id)}
        />
      ),
    },
    {
      title: "Edit",
      key: "edit",
      render: (_: any, record: PermissionWithFlags) => (
        <Checkbox
          name="edit"
          checked={record.edit}
          disabled={!record.supportsEdit}
          onChange={(e) => onChangePermission(e, record.id)}
        />
      ),
    },
    {
      title: "Delete",
      key: "delete",
      render: (_: any, record: PermissionWithFlags) => (
        <Checkbox
          name="delete"
          checked={record.delete}
          disabled={!record.supportsDelete}
          onChange={(e) => onChangePermission(e, record.id)}
        />
      ),
    },
  ];

  const grouped = groupAndSortByKey(displayedPermissions, "category");

  return (
    <>
      <ActionButton
        onClick={handlers.openEditDrawer}
        text="Edit Role"
        type="primary"
        disabled={!hasPermissions}
        icon={<EditOutlined />}
      />

      <Drawer
        title={`Edit Role: ${editingRow?.name || ""}`}
        placement="right"
        onClose={closeEditDrawer}
        open={visibleEditDrawer}
        width={500}
        className="manage-pages-drawer"
        forceRender
      >
        {editingRow && (
          <Form
            form={editingForm}
            layout="vertical"
            onFinish={(values) => {
              // Remove old permissions if present
              const { permissions, ...restValues } = values;

              // Only include permissions where at least one action is true
              const filteredPermissions = updatedPermissions.filter(
                (perm) => perm.read || perm.create || perm.edit || perm.delete
              );
              onFinishEditing(
                restValues,
                editingRow.id,
                filteredPermissions
              );
            }}
            initialValues={{
              nameValue: editingRow.name,
              descriptionValue: editingRow.description,
              customerApplicationValue: editingRow.customerApplication,
              isActiveValue: editingRow.isActive,
              dashboardsValue: editingRow.dashboards ? editingRow.dashboards : []
            }}
            ref={editFormRef}
            className="edit-role-form"
          >
            <Form.Item
              name="nameValue"
              label="Name"
              rules={[{ required: true, message: "Please enter role name" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="descriptionValue"
              label="Description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              name="isActiveValue"
              label="Active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="customerApplicationValue"
              label="Customer Application"
              rules={[{ required: true, message: "Please select application" }]}
            >
              <Select onChange={handleChangeApplication} placeholder="Select application">
                {applicationsList
                  .filter((app) => app.active)
                  .map((app) => (
                    <Option key={app._id} value={app._id}>
                      {app.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item name="dashboardsValue" label="Dashboards">
            {editingForm.getFieldValue("customerApplicationValue") ? (
              displayedDashboards.length ? (
                  <Select mode="multiple" placeholder="Select dashboards">
                    {displayedDashboards.map((dashboard) => (
                      <Select.Option
                        key={dashboard.id}
                        value={dashboard.id}
                        title={dashboard.name}
                      >
                        {dashboard.name}
                      </Select.Option>
                    ))}
                  </Select>
              ) : (
                <p>No dashboards for this Customer Application.</p>
              )
            ) : (
              <p>Please select Customer Application to see the dashboards.</p>
            )}
            </Form.Item>


            <Form.Item label="Permissions">
              {Object.entries(grouped).map(([category, perms]) => (
                <Table
                  key={category}
                  title={() => category}
                  columns={permissionColumns}
                  dataSource={perms as PermissionWithFlags[]}
                  pagination={false}
                  rowKey="id"
                  className="role-table"
                />
              ))}
            </Form.Item>

            <Button type="primary" htmlType="submit">
              Update Role
            </Button>
          </Form>
        )}
      </Drawer>
    </>
  );
};

export default EditRole;
